import { formatTRY } from "@/lib/currency";
import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import {
  Beer,
  CakeSlice,
  Coffee,
  Cookie,
  Croissant,
  Droplet,
  Gift,
  GlassWater,
  IceCream,
  Leaf,
  Pizza,
  Sandwich,
  Smile,
  Soup,
  Star,
  Utensils,
  Search,
  Plus,
  Edit2,
  Trash2,
  X,
  PlusCircle,
  FileText,
  Printer,
  Tag,
  Sparkles,
  Sliders,
} from "lucide-react";
import { toast } from "sonner";

import {
  categories as initialCategories,
  products as initialProducts,
  type Category,
  type Product,
} from "@/components/dashboard/data";
import { Button } from "@pos-vidyon/ui/components/button";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";

const menuSearchSchema = z.object({
  activeTab: z
    .enum(["products", "categories", "options", "stations"])
    .catch("products")
    .default("products"),
  productCategoryFilter: z.string().catch("all").default("all"),
  productSearch: z.string().catch("").default(""),
  productStatusFilter: z
    .enum(["all", "active", "inactive", "favorites"])
    .catch("all")
    .default("all"),
  categorySearch: z.string().catch("").default(""),
  selectedOptionGroupId: z.string().catch("milk").default("milk"),
});

export const Route = createFileRoute("/_auth/admin/menu")({
  validateSearch: menuSearchSchema,
  component: FullMenuManagementPage,
});

// Color palettes for product & category cards
const ICON_COLORS = [
  { name: "Sarı", class: "bg-yellow-100 text-yellow-805" },
  { name: "Turuncu", class: "bg-orange-100 text-orange-850" },
  { name: "Kırmızı", class: "bg-red-100 text-red-800" },
  { name: "Pembe", class: "bg-pink-100 text-pink-700" },
  { name: "Gül", class: "bg-rose-100 text-rose-800" },
  { name: "Mavi", class: "bg-gray-100 text-gray-800" },
  { name: "Gök Mavisi", class: "bg-sky-100 text-sky-805" },
  { name: "Yeşil", class: "bg-green-100 text-green-800" },
  { name: "Zümrüt", class: "bg-emerald-100 text-emerald-850" },
  { name: "Teal", class: "bg-teal-100 text-teal-800" },
  { name: "Kehribar", class: "bg-amber-100 text-amber-900" },
  { name: "Kahverengi", class: "bg-amber-200 text-amber-955" },
  { name: "Gri/Taş", class: "bg-stone-200 text-stone-800" },
  { name: "Koyu Taş", class: "bg-stone-900 text-white" },
];

const AVAILABLE_ICONS = [
  { name: "Kahve", icon: Coffee },
  { name: "Çay / Yaprak", icon: Leaf },
  { name: "Soğuk İçecek", icon: GlassWater },
  { name: "Kruvasan", icon: Croissant },
  { name: "Pasta Dilimi", icon: CakeSlice },
  { name: "Sandviç", icon: Sandwich },
  { name: "Pizza", icon: Pizza },
  { name: "Çorba / Kase", icon: Soup },
  { name: "Bira", icon: Beer },
  { name: "Dondurma", icon: IceCream },
  { name: "Kurabiye", icon: Cookie },
  { name: "Yemek / Çatal-Bıçak", icon: Utensils },
  { name: "Gülümseme", icon: Smile },
  { name: "Hediye", icon: Gift },
  { name: "Su Damlası", icon: Droplet },
  { name: "Yıldız", icon: Star },
];

interface OptionItem {
  id: string;
  name: string;
  priceDelta: number;
}

interface OptionGroup {
  id: string;
  name: string;
  minSelection: number;
  maxSelection: number;
  items: OptionItem[];
}

interface PrepStation {
  id: string;
  name: string;
  printerName: string;
  isActive: boolean;
  stationType: "drinks" | "food" | "bakery" | "grill" | "general";
}

interface ExtendedProduct extends Product {
  description?: string;
  isActive?: boolean;
  stationId?: string;
  optionGroupIds?: string[];
}

function FullMenuManagementPage() {
  const {
    activeTab,
    productCategoryFilter,
    productSearch,
    productStatusFilter,
    categorySearch,
    selectedOptionGroupId,
  } = Route.useSearch();

  const navigate = useNavigate({ from: Route.fullPath });

  const setFilter = (updates: Partial<z.infer<typeof menuSearchSchema>>) => {
    navigate({
      search: (prev) => ({
        ...prev,
        ...updates,
      }),
    });
  };

  // --- Core Mock States ---
  const [products, setProducts] = useState<ExtendedProduct[]>(() => {
    return initialProducts.map((p) => ({
      ...p,
      isActive: true,
      description: `${p.name} prepared fresh with top quality ingredients by our team.`,
      stationId:
        p.categoryId === "coffee" ||
        p.categoryId === "tea" ||
        p.categoryId === "cold-drinks" ||
        p.categoryId === "beer-wine" ||
        p.categoryId === "cocktails"
          ? "bar"
          : "kitchen",
      optionGroupIds: p.categoryId === "coffee" ? ["milk", "size"] : [],
    }));
  });

  const [categories, setCategories] = useState<Category[]>(() => initialCategories);

  const [optionGroups, setOptionGroups] = useState<OptionGroup[]>([
    {
      id: "milk",
      name: "Süt Seçimi",
      minSelection: 0,
      maxSelection: 1,
      items: [
        { id: "milk-1", name: "Normal Süt", priceDelta: 0.0 },
        { id: "milk-2", name: "Soya Sütü", priceDelta: 0.5 },
        { id: "milk-3", name: "Badem Sütü", priceDelta: 0.75 },
        { id: "milk-4", name: "Yulaf Sütü", priceDelta: 0.7 },
        { id: "milk-5", name: "Laktozsuz Süt", priceDelta: 0.0 },
      ],
    },
    {
      id: "size",
      name: "Porsiyon / Boyut",
      minSelection: 1,
      maxSelection: 1,
      items: [
        { id: "size-1", name: "Standart / Orta", priceDelta: 0.0 },
        { id: "size-2", name: "Büyük Boy", priceDelta: 1.0 },
      ],
    },
    {
      id: "extras",
      name: "Ekstralar",
      minSelection: 0,
      maxSelection: 5,
      items: [
        { id: "ext-1", name: "Ekstra Shot Espresso", priceDelta: 1.0 },
        { id: "ext-2", name: "Krema / Krem Şanti", priceDelta: 0.6 },
        { id: "ext-3", name: "Aroma Şurubu", priceDelta: 0.5 },
      ],
    },
  ]);

  const [stations, setStations] = useState<PrepStation[]>([
    {
      id: "kitchen",
      name: "Ana Mutfak",
      printerName: "KOT-1 (Epson TCP/IP)",
      isActive: true,
      stationType: "food",
    },
    {
      id: "bar",
      name: "Barista Tezgahı",
      printerName: "BAR-2 (USB Thermal)",
      isActive: true,
      stationType: "drinks",
    },
    {
      id: "bakery",
      name: "Fırın & Tatlı",
      printerName: "KOT-2 (Epson)",
      isActive: true,
      stationType: "bakery",
    },
  ]);

  // --- Modal Open State ---
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<ExtendedProduct | null>(null);

  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);

  const [isOptionGroupModalOpen, setIsOptionGroupModalOpen] = useState(false);
  const [editingOptionGroup, setEditingOptionGroup] = useState<OptionGroup | null>(null);

  const [isStationModalOpen, setIsStationModalOpen] = useState(false);
  const [editingStation, setEditingStation] = useState<PrepStation | null>(null);

  // --- Form Input States ---
  // Product Form
  const [prodFormName, setProdFormName] = useState("");
  const [prodFormPrice, setProdFormPrice] = useState(0.0);
  const [prodFormCategoryId, setProdFormCategoryId] = useState("");
  const [prodFormIconName, setProdFormIconName] = useState("Kahve");
  const [prodFormColorClass, setProdFormColorClass] = useState("bg-amber-100 text-amber-700");
  const [prodFormDesc, setProdFormDesc] = useState("");
  const [prodFormActive, setProdFormActive] = useState(true);
  const [prodFormFavorite, setProdFormFavorite] = useState(false);
  const [prodFormStation, setProdFormStation] = useState("bar");
  const [prodFormOptionGroups, setProdFormOptionGroups] = useState<string[]>([]);

  // Category Form
  const [catFormLabel, setCatFormLabel] = useState("");
  const [catFormIconName, setCatFormIconName] = useState("Kahve");

  // Option Group Form
  const [optGroupName, setOptGroupName] = useState("");
  const [optGroupMin, setOptGroupMin] = useState(0);
  const [optGroupMax, setOptGroupMax] = useState(1);
  const [optGroupItems, setOptGroupItems] = useState<OptionItem[]>([]);

  // Station Form
  const [stationName, setStationName] = useState("");
  const [stationPrinter, setStationPrinter] = useState("");
  const [stationType, setStationType] = useState<PrepStation["stationType"]>("general");
  const [stationActive, setStationActive] = useState(true);

  // --- Filtered Products (inside tab 1) ---
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesCategory =
        productCategoryFilter === "all" || p.categoryId === productCategoryFilter;
      const matchesSearch =
        p.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        (p.description && p.description.toLowerCase().includes(productSearch.toLowerCase()));

      let matchesStatus = true;
      if (productStatusFilter === "active") matchesStatus = Boolean(p.isActive);
      else if (productStatusFilter === "inactive") matchesStatus = !p.isActive;
      else if (productStatusFilter === "favorites") matchesStatus = Boolean(p.isFavorite);

      return matchesCategory && matchesSearch && matchesStatus;
    });
  }, [products, productCategoryFilter, productSearch, productStatusFilter]);

  // --- Filtered Categories (inside tab 2) ---
  const filteredCategories = useMemo(() => {
    return categories.filter(
      (c) =>
        c.label.toLowerCase().includes(categorySearch.toLowerCase()) ||
        c.id.toLowerCase().includes(categorySearch.toLowerCase()),
    );
  }, [categories, categorySearch]);

  // --- Product Form Handlers ---
  const openAddProduct = () => {
    setEditingProduct(null);
    setProdFormName("");
    setProdFormPrice(4.0);
    setProdFormCategoryId(
      productCategoryFilter !== "all" ? productCategoryFilter : categories[0]?.id || "coffee",
    );
    setProdFormIconName("Kahve");
    setProdFormColorClass("bg-amber-100 text-amber-700");
    setProdFormDesc("");
    setProdFormActive(true);
    setProdFormFavorite(false);
    setProdFormStation("bar");
    setProdFormOptionGroups([]);
    setIsProductModalOpen(true);
  };

  const openEditProduct = (p: ExtendedProduct) => {
    setEditingProduct(p);
    setProdFormName(p.name);
    setProdFormPrice(p.price);
    setProdFormCategoryId(p.categoryId);
    const matched = AVAILABLE_ICONS.find((item) => item.icon === p.icon);
    setProdFormIconName(matched ? matched.name : "Kahve");
    setProdFormColorClass(p.iconClassName);
    setProdFormDesc(p.description || "");
    setProdFormActive(p.isActive !== false);
    setProdFormFavorite(Boolean(p.isFavorite));
    setProdFormStation(p.stationId || "bar");
    setProdFormOptionGroups(p.optionGroupIds || []);
    setIsProductModalOpen(true);
  };

  const saveProduct = () => {
    if (!prodFormName.trim()) {
      toast.error("Ürün adı girmelisiniz.");
      return;
    }
    const matchedIconObj = AVAILABLE_ICONS.find((item) => item.name === prodFormIconName);
    const iconToSave = matchedIconObj ? matchedIconObj.icon : Coffee;

    const data: Partial<ExtendedProduct> = {
      name: prodFormName,
      price: prodFormPrice,
      categoryId: prodFormCategoryId,
      icon: iconToSave,
      iconClassName: prodFormColorClass,
      description: prodFormDesc,
      isActive: prodFormActive,
      isFavorite: prodFormFavorite,
      stationId: prodFormStation,
      optionGroupIds: prodFormOptionGroups,
    };

    if (editingProduct) {
      setProducts((prev) => prev.map((p) => (p.id === editingProduct.id ? { ...p, ...data } : p)));
      toast.success(`"${prodFormName}" güncellendi.`);
    } else {
      const newId =
        prodFormName.toLowerCase().replace(/[^a-z0-9]/g, "-") +
        "-" +
        Date.now().toString().slice(-4);
      setProducts((prev) => [{ id: newId, ...data } as ExtendedProduct, ...prev]);
      toast.success(`"${prodFormName}" menüye eklendi.`);
    }
    setIsProductModalOpen(false);
  };

  const deleteProduct = (id: string, name: string) => {
    if (confirm(`"${name}" ürününü menüden kaldırmak istediğinize emin misiniz?`)) {
      setProducts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Ürün silindi.");
    }
  };

  // --- Category Form Handlers ---
  const openAddCategory = () => {
    setEditingCategory(null);
    setCatFormLabel("");
    setCatFormIconName("Kahve");
    setIsCategoryModalOpen(true);
  };

  const openEditCategory = (c: Category) => {
    setEditingCategory(c);
    setCatFormLabel(c.label);
    const matched = AVAILABLE_ICONS.find((item) => item.icon === c.icon);
    setCatFormIconName(matched ? matched.name : "Kahve");
    setIsCategoryModalOpen(true);
  };

  const saveCategory = () => {
    if (!catFormLabel.trim()) {
      toast.error("Kategori adı boş olamaz.");
      return;
    }
    const matched =
      AVAILABLE_ICONS.find((item) => item.name === catFormIconName) || AVAILABLE_ICONS[0];

    if (editingCategory) {
      setCategories((prev) =>
        prev.map((c) =>
          c.id === editingCategory.id ? { ...c, label: catFormLabel, icon: matched.icon } : c,
        ),
      );
      toast.success("Kategori başlığı güncellendi.");
    } else {
      const newId =
        catFormLabel.toLowerCase().replace(/[^a-z0-9]/g, "-") +
        "-" +
        Date.now().toString().slice(-4);
      setCategories((prev) => [
        ...prev,
        {
          id: newId,
          label: catFormLabel,
          icon: matched.icon,
          image:
            "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=600&auto=format&fit=crop",
        },
      ]);
      toast.success("Kategori oluşturuldu.");
    }
    setIsCategoryModalOpen(false);
  };

  const deleteCategory = (id: string, label: string) => {
    const usageCount = products.filter((p) => p.categoryId === id).length;
    if (usageCount > 0) {
      toast.error(`"${label}" silinemez. Bu kategoriye ait ${usageCount} ürün mevcut.`);
      return;
    }
    if (confirm(`"${label}" kategorisini silmek istediğinizden emin misiniz?`)) {
      setCategories((prev) => prev.filter((c) => c.id !== id));
      toast.success("Kategori silindi.");
    }
  };

  // --- Option Group Handlers ---
  const openAddOptionGroup = () => {
    setEditingOptionGroup(null);
    setOptGroupName("");
    setOptGroupMin(0);
    setOptGroupMax(1);
    setOptGroupItems([]);
    setIsOptionGroupModalOpen(true);
  };

  const openEditOptionGroup = (g: OptionGroup) => {
    setEditingOptionGroup(g);
    setOptGroupName(g.name);
    setOptGroupMin(g.minSelection);
    setOptGroupMax(g.maxSelection);
    setOptGroupItems([...g.items]);
    setIsOptionGroupModalOpen(true);
  };

  const saveOptionGroup = () => {
    if (!optGroupName.trim()) {
      toast.error("Seçenek grubu adı girin.");
      return;
    }

    const data: OptionGroup = {
      id: editingOptionGroup
        ? editingOptionGroup.id
        : optGroupName.toLowerCase().replace(/[^a-z0-9]/g, "-") +
          "-" +
          Date.now().toString().slice(-4),
      name: optGroupName,
      minSelection: optGroupMin,
      maxSelection: optGroupMax,
      items: optGroupItems,
    };

    if (editingOptionGroup) {
      setOptionGroups((prev) => prev.map((g) => (g.id === editingOptionGroup.id ? data : g)));
      toast.success("Seçenek grubu güncellendi.");
    } else {
      setOptionGroups((prev) => [...prev, data]);
      setFilter({ selectedOptionGroupId: data.id });
      toast.success("Seçenek grubu eklendi.");
    }
    setIsOptionGroupModalOpen(false);
  };

  // Option items rows inside modifier detail pane
  const addOptionItemRow = () => {
    const newId =
      "item-" + Date.now().toString().slice(-4) + Math.random().toString(36).substr(2, 4);
    setOptGroupItems((prev) => [...prev, { id: newId, name: "Yeni Seçenek", priceDelta: 0.0 }]);
  };

  const removeOptionItemRow = (itemId: string) => {
    setOptGroupItems((prev) => prev.filter((i) => i.id !== itemId));
  };

  const updateOptionItemRow = (itemId: string, field: keyof OptionItem, value: any) => {
    setOptGroupItems((prev) => prev.map((i) => (i.id === itemId ? { ...i, [field]: value } : i)));
  };

  const deleteOptionGroup = (id: string, name: string) => {
    if (confirm(`"${name}" grubunu silmek istediğinizden emin misiniz?`)) {
      setOptionGroups((prev) => prev.filter((g) => g.id !== id));
      // Clean product references
      setProducts((prev) =>
        prev.map((p) => ({
          ...p,
          optionGroupIds: p.optionGroupIds?.filter((gid) => gid !== id) || [],
        })),
      );
      toast.success("Grup silindi.");
    }
  };

  // --- Station Handlers ---
  const openAddStation = () => {
    setEditingStation(null);
    setStationName("");
    setStationPrinter("");
    setStationType("general");
    setStationActive(true);
    setIsStationModalOpen(true);
  };

  const openEditStation = (s: PrepStation) => {
    setEditingStation(s);
    setStationName(s.name);
    setStationPrinter(s.printerName);
    setStationType(s.stationType);
    setStationActive(s.isActive);
    setIsStationModalOpen(true);
  };

  const saveStation = () => {
    if (!stationName.trim()) {
      toast.error("İstasyon adı girilmelidir.");
      return;
    }

    const data: PrepStation = {
      id: editingStation
        ? editingStation.id
        : stationName.toLowerCase().replace(/[^a-z0-9]/g, "-") +
          "-" +
          Date.now().toString().slice(-4),
      name: stationName,
      printerName: stationPrinter || "Yazıcı Bağlı Değil",
      stationType,
      isActive: stationActive,
    };

    if (editingStation) {
      setStations((prev) => prev.map((s) => (s.id === editingStation.id ? data : s)));
      toast.success("İstasyon güncellendi.");
    } else {
      setStations((prev) => [...prev, data]);
      toast.success("İstasyon eklendi.");
    }
    setIsStationModalOpen(false);
  };

  const deleteStation = (id: string, name: string) => {
    const isUsed = products.some((p) => p.stationId === id);
    if (isUsed) {
      toast.error(`"${name}" silinemez. Bu istasyona yönlendirilen ürünler mevcut.`);
      return;
    }
    if (confirm(`"${name}" hazırlık istasyonunu silmek istiyor musunuz?`)) {
      setStations((prev) => prev.filter((s) => s.id !== id));
      toast.success("İstasyon silindi.");
    }
  };

  // --- Live product card preview rendering ---
  const livePreviewIconObj =
    AVAILABLE_ICONS.find((item) => item.name === prodFormIconName) || AVAILABLE_ICONS[0];
  const LivePreviewIconComp = livePreviewIconObj.icon;

  return (
    <div className="space-y-4 pb-12 font-sans w-full">
      {/* 1. Page Header (Matched to general architectural style with buttons pushed to far right) */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between w-full">
        <div>
          <h1 className="text-2xl font-black tracking-tight text-gray-900">
            Detaylı Menü Yönetimi
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Ürün kartlarını, kategorileri, ekstra seçenek gruplarını ve mutfak yazıcı istasyonlarını
            düzenleyin.
          </p>
        </div>

        {/* Global Import/Export aligned far right via sm:ml-auto */}
        <div className="flex items-center gap-2.5 sm:ml-auto">
          <Button
            variant="outline"
            className="flex items-center gap-1.5 text-xs font-bold bg-white text-gray-700 border-gray-200 hover:bg-gray-55 shadow-sm"
            onClick={() => {
              const dataStr = JSON.stringify(
                { categories, products, optionGroups, stations },
                null,
                2,
              );
              const dataUri = "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);
              const link = document.createElement("a");
              link.setAttribute("href", dataUri);
              link.setAttribute(
                "download",
                `full-menu-config-${new Date().toISOString().slice(0, 10)}.json`,
              );
              link.click();
              toast.success("Tüm menü konfigürasyonu dışa aktarıldı!");
            }}
          >
            <FileText className="size-3.5 text-gray-600" />
            <span>Yedek Al (Export)</span>
          </Button>

          <Button
            className="flex items-center gap-1.5 text-xs font-bold bg-gray-900 text-white hover:bg-gray-800 shadow-sm"
            onClick={() => {
              if (activeTab === "products") openAddProduct();
              else if (activeTab === "categories") openAddCategory();
              else if (activeTab === "options") openAddOptionGroup();
              else if (activeTab === "stations") openAddStation();
            }}
          >
            <Plus className="size-4" />
            <span>
              {activeTab === "products" && "Yeni Ürün Ekle"}
              {activeTab === "categories" && "Yeni Kategori Ekle"}
              {activeTab === "options" && "Yeni Grup Ekle"}
              {activeTab === "stations" && "Yeni İstasyon Ekle"}
            </span>
          </Button>
        </div>
      </div>

      {/* 2. Top Segmented Segment Selection */}
      <div className="flex bg-white p-1 rounded-2xl border border-gray-200 max-w-2xl shadow-sm overflow-x-auto">
        <button
          onClick={() => setFilter({ activeTab: "products" })}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-xs font-bold rounded-xl transition-all focus:outline-none whitespace-nowrap ${
            activeTab === "products"
              ? "bg-gray-900 text-white shadow-sm"
              : "text-gray-500 hover:text-gray-800"
          }`}
        >
          <Utensils className="size-4" />
          <span>Ürün Yönetimi ({products.length})</span>
        </button>

        <button
          onClick={() => setFilter({ activeTab: "categories" })}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-xs font-bold rounded-xl transition-all focus:outline-none whitespace-nowrap ${
            activeTab === "categories"
              ? "bg-gray-900 text-white shadow-sm"
              : "text-gray-500 hover:text-gray-800"
          }`}
        >
          <Tag className="size-4" />
          <span>Kategoriler ({categories.length})</span>
        </button>

        <button
          onClick={() => setFilter({ activeTab: "options" })}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-xs font-bold rounded-xl transition-all focus:outline-none whitespace-nowrap ${
            activeTab === "options"
              ? "bg-gray-900 text-white shadow-sm"
              : "text-gray-500 hover:text-gray-800"
          }`}
        >
          <Sparkles className="size-4" />
          <span>Seçenek Grupları ({optionGroups.length})</span>
        </button>

        <button
          onClick={() => setFilter({ activeTab: "stations" })}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-xs font-bold rounded-xl transition-all focus:outline-none whitespace-nowrap ${
            activeTab === "stations"
              ? "bg-gray-900 text-white shadow-sm"
              : "text-gray-500 hover:text-gray-800"
          }`}
        >
          <Printer className="size-4" />
          <span>Hazırlık İstasyonları ({stations.length})</span>
        </button>
      </div>

      {/* 3. Tab Contents Layouts */}
      <main className="min-h-[500px] w-full">
        {/* --- PRODUCTS TAB (FULL-WIDTH DATA-TABLE WITH CATEGORY SELECTBOX) --- */}
        {activeTab === "products" && (
          <div className="w-full space-y-4">
            {/* Products Filter Bar (Search + Dropdowns) */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex flex-col gap-3 md:flex-row md:items-center justify-between">
              {/* Search input */}
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-2.5 size-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Ürün adı veya açıklama ara..."
                  value={productSearch}
                  onChange={(e) => setFilter({ productSearch: e.target.value })}
                  className="w-full pl-9 pr-4 py-1.5 text-xs rounded-xl border border-gray-200 bg-gray-55 focus:outline-none focus:border-gray-700 focus:bg-white"
                />
              </div>

              {/* Dropdown Select Boxes */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Category Select Filter - UX Refinement */}
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400 font-bold uppercase">Kategori:</span>
                  <select
                    value={productCategoryFilter}
                    onChange={(e) => setFilter({ productCategoryFilter: e.target.value })}
                    className="text-xs font-bold bg-stone-50 border border-stone-200 rounded-xl p-2 focus:outline-none focus:border-gray-700"
                  >
                    <option value="all">Tüm Kategoriler</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label} ({products.filter((p) => p.categoryId === c.id).length})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Select Filter */}
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-gray-400 font-bold uppercase">Durum:</span>
                  <select
                    value={productStatusFilter}
                    onChange={(e) => setFilter({ productStatusFilter: e.target.value as any })}
                    className="text-xs font-bold bg-stone-50 border border-stone-200 rounded-xl p-2 focus:outline-none focus:border-gray-700"
                  >
                    <option value="all">Tüm Durumlar</option>
                    <option value="active">Yalnızca Satıştaki Aktifler</option>
                    <option value="inactive">Pasif / Tükendi</option>
                    <option value="favorites">Favori Ürünler</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Data Table */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
              {filteredProducts.length === 0 ? (
                <div className="text-center py-20 bg-gray-50/50">
                  <Search className="size-8 text-gray-300 mx-auto mb-2" />
                  <h4 className="text-sm font-bold text-gray-800">Ürün Bulunmadı</h4>
                  <p className="text-xs text-gray-400 mt-1">
                    Arama teriminizi veya filtre ayarınızı değiştirmeyi deneyin.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 text-[10px] font-bold uppercase text-gray-400 tracking-wider bg-gray-50/75 select-none">
                        <th className="py-4 px-6 w-12 text-center">Görsel</th>
                        <th className="py-4 px-6">Ürün Detayı</th>
                        <th className="py-4 px-6 w-32">Fiyat</th>
                        <th className="py-4 px-6 w-40">Hazırlık Yeri</th>
                        <th className="py-4 px-6 w-32">Seçenekler</th>
                        <th className="py-4 px-6 w-20 text-center">Favori</th>
                        <th className="py-4 px-6 w-32">Durum</th>
                        <th className="py-4 px-6 w-24 text-right">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-150">
                      {filteredProducts.map((product) => {
                        const ProductIcon = product.icon || Coffee;
                        const categoryLabel =
                          categories.find((c) => c.id === product.categoryId)?.label ||
                          "Bilinmeyen";
                        const station = stations.find((s) => s.id === product.stationId);
                        const stationLabel = station ? station.name : "Atanmamış";

                        return (
                          <tr
                            key={product.id}
                            className={`group hover:bg-slate-50/70 transition-all ${
                              !product.isActive ? "opacity-60 bg-gray-50/30" : ""
                            }`}
                          >
                            {/* Product Icon badge */}
                            <td className="py-5 px-6 text-center">
                              <div
                                className={`flex size-9 mx-auto items-center justify-center rounded-lg shadow-inner font-semibold ${product.iconClassName || "bg-gray-155"}`}
                              >
                                <ProductIcon className="size-4.5" />
                              </div>
                            </td>

                            {/* Title & Description */}
                            <td className="py-5 px-6">
                              <div>
                                <span className="text-sm font-bold text-gray-900 block leading-tight">
                                  {product.name}
                                </span>
                                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider block mt-1">
                                  {categoryLabel}
                                </span>
                              </div>
                            </td>

                            {/* Price */}
                            <td className="py-5 px-6 font-black text-sm text-gray-850">
                              {formatTRY(product.price)}
                            </td>

                            {/* Station Tag */}
                            <td className="py-5 px-6">
                              <span
                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[9px] font-bold border ${
                                  product.stationId === "bar"
                                    ? "bg-amber-50 text-amber-800 border-amber-200"
                                    : "bg-gray-50 text-gray-800 border-gray-200"
                                }`}
                              >
                                <Printer className="size-2.5" />
                                {stationLabel}
                              </span>
                            </td>

                            {/* Modifiers Count Badge */}
                            <td className="py-5 px-6">
                              {product.optionGroupIds && product.optionGroupIds.length > 0 ? (
                                <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold text-slate-650 border border-slate-200">
                                  <Sliders className="size-2.5" />
                                  {product.optionGroupIds.length} Seçenek
                                </span>
                              ) : (
                                <span className="text-[10px] text-gray-405 font-semibold italic">
                                  Yok
                                </span>
                              )}
                            </td>

                            {/* Favorite Toggle button */}
                            <td className="py-5 px-6 text-center">
                              <button
                                onClick={() => {
                                  setProducts((prev) =>
                                    prev.map((item) =>
                                      item.id === product.id
                                        ? { ...item, isFavorite: !item.isFavorite }
                                        : item,
                                    ),
                                  );
                                }}
                                className={`p-1 rounded-lg hover:bg-gray-105 transition-colors ${
                                  product.isFavorite
                                    ? "text-amber-500"
                                    : "text-gray-300 hover:text-gray-400"
                                }`}
                              >
                                <Star className="size-4.5 fill-current" />
                              </button>
                            </td>

                            {/* Stock Switch */}
                            <td className="py-5 px-6">
                              <button
                                onClick={() => {
                                  setProducts((prev) =>
                                    prev.map((item) =>
                                      item.id === product.id
                                        ? { ...item, isActive: !item.isActive }
                                        : item,
                                    ),
                                  );
                                  toast.info(`"${product.name}" satış durumu güncellendi.`);
                                }}
                                className="inline-flex items-center gap-1.5 focus:outline-none"
                              >
                                <span
                                  className={`size-2 rounded-full relative ${
                                    product.isActive ? "bg-emerald-500" : "bg-rose-450"
                                  }`}
                                >
                                  {product.isActive && (
                                    <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-75" />
                                  )}
                                </span>
                                <span className="text-xs font-semibold text-gray-655">
                                  {product.isActive ? "Satışta" : "Tükendi"}
                                </span>
                              </button>
                            </td>

                            {/* Action buttons */}
                            <td className="py-5 px-6 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => openEditProduct(product)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                                  title="Düzenle"
                                >
                                  <Edit2 className="size-3.5" />
                                </button>
                                <button
                                  onClick={() => deleteProduct(product.id, product.name)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                  title="Sil"
                                >
                                  <Trash2 className="size-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Table Footer info */}
                  <div className="py-3.5 px-6 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-500 select-none">
                    <span>
                      Toplam <b>{filteredProducts.length}</b> üründen 1 - {filteredProducts.length}{" "}
                      arası gösteriliyor
                    </span>
                    <span className="font-semibold text-gray-400">
                      Vidyon POS Menü Yönetimi v1.2
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* --- CATEGORIES TAB (DATA-TABLE REDESIGN) --- */}
        {activeTab === "categories" && (
          <div className="w-full space-y-4">
            {/* Categories filter/search and add action bar */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm flex flex-col gap-3 md:flex-row md:items-center justify-between">
              {/* Search input */}
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-2.5 size-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Kategori adı veya ID ara..."
                  value={categorySearch}
                  onChange={(e) => setFilter({ categorySearch: e.target.value })}
                  className="w-full pl-9 pr-4 py-1.5 text-xs rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:border-gray-700 focus:bg-white"
                />
              </div>

              <div className="text-xs text-gray-400 font-bold uppercase select-none">
                Toplam Kategori: {categories.length}
              </div>
            </div>

            {/* Categories Data Table */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
              {filteredCategories.length === 0 ? (
                <div className="text-center py-20 bg-gray-50/50">
                  <Search className="size-8 text-gray-300 mx-auto mb-2" />
                  <h4 className="text-sm font-bold text-gray-800">Kategori Bulunmadı</h4>
                  <p className="text-xs text-gray-400 mt-1">
                    Arama teriminizi değiştirmeyi deneyin.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="border-b border-gray-200 text-[10px] font-bold uppercase text-gray-400 tracking-wider bg-gray-50/75 select-none">
                        <th className="py-4 px-6 w-12 text-center">İkon</th>
                        <th className="py-4 px-6">Kategori Adı</th>
                        <th className="py-4 px-6 w-64">Sistem ID / Kod</th>
                        <th className="py-4 px-6 w-48">Bağlı Ürün Sayısı</th>
                        <th className="py-4 px-6 w-24 text-right">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-150">
                      {filteredCategories.map((cat) => {
                        const CatIcon = cat.icon || Coffee;
                        const count = products.filter((p) => p.categoryId === cat.id).length;

                        return (
                          <tr key={cat.id} className="group hover:bg-slate-50/70 transition-all">
                            {/* Icon */}
                            <td className="py-5 px-6 text-center">
                              <div className="flex size-9 mx-auto items-center justify-center rounded-lg bg-gray-50 text-gray-900 shadow-inner">
                                <CatIcon className="size-4.5" />
                              </div>
                            </td>

                            {/* Label */}
                            <td className="py-5 px-6">
                              <span className="text-sm font-bold text-gray-900 leading-tight">
                                {cat.label}
                              </span>
                            </td>

                            {/* Monospace Code ID */}
                            <td className="py-5 px-6">
                              <code className="text-xs  text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                {cat.id}
                              </code>
                            </td>

                            {/* Product Count Badge */}
                            <td className="py-5 px-6">
                              <span className="inline-flex items-center rounded-full bg-gray-50 px-2.5 py-0.5 text-[10px] font-bold text-gray-800 border border-gray-200 select-none">
                                {count} Ürün Aktif
                              </span>
                            </td>

                            {/* Actions */}
                            <td className="py-5 px-6 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button
                                  onClick={() => openEditCategory(cat)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
                                  title="Düzenle"
                                >
                                  <Edit2 className="size-3.5" />
                                </button>
                                <button
                                  onClick={() => deleteCategory(cat.id, cat.label)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                                  title="Sil"
                                >
                                  <Trash2 className="size-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Footer */}
                  <div className="py-3.5 px-6 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 select-none">
                    Kategoriler, POS terminal arayüzündeki dikey ray menüsünün sıralamasını ve
                    yapısını belirler.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* --- OPTION GROUPS (MASTER-DETAIL LAYOUT) --- */}
        {activeTab === "options" && (
          <div className="flex flex-col lg:flex-row gap-5 items-start">
            {/* Master Option Groups list (Left) */}
            <div className="w-full lg:w-[320px] shrink-0 bg-white border border-gray-250 rounded-2xl p-4 shadow-sm space-y-3">
              <div className="flex items-center justify-between px-1">
                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  Seçenek Grupları
                </h3>
                <button
                  onClick={openAddOptionGroup}
                  className="text-xs font-bold text-gray-600 hover:text-gray-800 flex items-center gap-0.5"
                >
                  <Plus className="size-3" />
                  Ekle
                </button>
              </div>

              <div className="space-y-1">
                {optionGroups.map((group) => {
                  const isSelected = group.id === selectedOptionGroupId;

                  return (
                    <div
                      key={group.id}
                      onClick={() => setFilter({ selectedOptionGroupId: group.id })}
                      className={`flex items-center justify-between rounded-xl p-2.5 cursor-pointer transition-all border ${
                        isSelected
                          ? "bg-gray-50 text-gray-800 border-gray-200 font-bold"
                          : "text-gray-700 hover:bg-gray-50 border-transparent"
                      }`}
                    >
                      <div className="min-w-0">
                        <span className="text-xs block font-bold leading-tight">{group.name}</span>
                        <span className="text-[9px] text-gray-400 font-medium mt-0.5">
                          {group.minSelection === 0 ? "İsteğe Bağlı" : "Zorunlu"} • Max:{" "}
                          {group.maxSelection}
                        </span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditOptionGroup(group);
                          }}
                          className="p-1 rounded text-gray-400 hover:text-gray-900 hover:bg-white"
                        >
                          <Edit2 className="size-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteOptionGroup(group.id, group.name);
                          }}
                          className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-white"
                        >
                          <Trash2 className="size-3" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Detail Pane: Modifier Items table (Right) */}
            <div className="flex-1 w-full bg-white border border-gray-200 rounded-2xl p-5 shadow-sm space-y-4">
              {(() => {
                const group = optionGroups.find((g) => g.id === selectedOptionGroupId);
                if (!group) {
                  return (
                    <div className="text-center py-20 text-gray-400 text-xs">
                      Detayları görmek için soldan bir seçenek grubu seçin veya yeni ekleyin.
                    </div>
                  );
                }

                const linkedProducts = products.filter((p) => p.optionGroupIds?.includes(group.id));

                return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between border-b border-gray-150 pb-3 flex-wrap gap-2">
                      <div>
                        <h3 className="font-extrabold text-base text-gray-900 flex items-center gap-2">
                          {group.name} Alt Seçenekleri
                          <span className="text-[9px] font-bold text-gray-600 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded">
                            {group.minSelection === 0
                              ? "Seçim İsteğe Bağlı"
                              : "En Az 1 Zorunlu Seçim"}{" "}
                            (Maks. {group.maxSelection})
                          </span>
                        </h3>
                        <p className="text-xs text-gray-400 mt-0.5">
                          Bu gruba ait özelleştirmeleri ve fiyat farklarını tanımlayın.
                        </p>
                      </div>

                      <Button
                        onClick={() => openEditOptionGroup(group)}
                        className="text-xs font-bold border-gray-200 hover:bg-gray-50 py-1.5 px-3"
                      >
                        Grup Detayını Düzenle
                      </Button>
                    </div>

                    {/* Modifiers List Table */}
                    {group.items.length === 0 ? (
                      <div className="text-center py-12 border border-dashed border-gray-200 rounded-xl text-xs text-gray-400 bg-gray-50/50">
                        Bu grupta alt seçenek öğesi bulunmuyor. Düzenle butonunu kullanarak seçenek
                        ekleyebilirsiniz.
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">
                          Alt Seçenekler & Fiyat Farkları
                        </span>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {group.items.map((item) => (
                            <div
                              key={item.id}
                              className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs font-semibold"
                            >
                              <span className="text-gray-800">{item.name}</span>
                              <span className="text-gray-900 bg-white border border-gray-150 px-2 py-0.5 rounded font-black">
                                {item.priceDelta > 0
                                  ? `+${formatTRY(item.priceDelta)}`
                                  : "Ücretsiz"}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Linked Products list */}
                    <div className="pt-4 border-t border-gray-100 text-xs">
                      <span className="block font-bold text-gray-400 uppercase text-[9px] tracking-wider mb-2">
                        Bu Seçeneği Kullanan Ürünler
                      </span>
                      {linkedProducts.length === 0 ? (
                        <span className="text-gray-400 italic">
                          Bu seçeneğe henüz herhangi bir ürün bağlanmamış.
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {linkedProducts.map((p) => (
                            <span
                              key={p.id}
                              className="bg-stone-100 border border-stone-200 text-stone-750 px-2 py-0.5 rounded-lg font-bold text-[10px]"
                            >
                              {p.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* --- PREPARATION STATIONS TAB --- */}
        {activeTab === "stations" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Dashed placeholder card to add new station */}
            <div
              onClick={openAddStation}
              className="rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50/50 hover:bg-gray-50 hover:border-gray-400 p-5 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all min-h-[160px]"
            >
              <Plus className="size-6 text-gray-400" />
              <span className="text-xs font-bold text-gray-500">Yeni İstasyon Ekle</span>
            </div>

            {stations.map((st) => {
              const linkedProducts = products.filter((p) => p.stationId === st.id);

              return (
                <div
                  key={st.id}
                  className={`rounded-2xl border bg-white p-5 shadow-sm hover:shadow transition-all duration-300 flex flex-col justify-between min-h-[160px] ${
                    !st.isActive
                      ? "border-gray-200 opacity-60 bg-gray-50/50"
                      : "border-gray-250/80 hover:border-gray-350"
                  }`}
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="flex size-9 items-center justify-center rounded-xl bg-gray-50 text-gray-900 shadow-inner">
                          <Printer className="size-4.5" />
                        </div>
                        <div>
                          <h4 className="font-extrabold text-sm text-gray-900 leading-tight">
                            {st.name}
                          </h4>
                          <span className="text-[9px] font-black uppercase text-gray-400 bg-gray-100 px-1 rounded mt-0.5 inline-block">
                            {st.stationType}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => openEditStation(st)}
                          className="p-1 rounded text-gray-400 hover:text-gray-600"
                        >
                          <Edit2 className="size-3" />
                        </button>
                        <button
                          onClick={() => deleteStation(st.id, st.name)}
                          className="p-1 rounded text-gray-400 hover:text-red-500"
                        >
                          <Trash2 className="size-3" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <div className="text-xs">
                        <span className="text-gray-400 font-bold uppercase text-[9px]">
                          Sistem Yazıcı Çıktısı
                        </span>
                        <p className=" text-gray-700 font-semibold">{st.printerName}</p>
                      </div>
                      <div className="text-xs">
                        <span className="text-gray-400 font-bold uppercase text-[9px]">
                          Aktif Yönlendirilen Ürünler
                        </span>
                        <p className="font-bold text-gray-900">{linkedProducts.length} Ürün</p>
                      </div>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs mt-1">
                    <span className="text-gray-400">Durum</span>
                    <span
                      className={`px-2 py-0.5 rounded-full font-bold text-[9px] uppercase tracking-wider ${
                        st.isActive
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {st.isActive ? "Çevrimiçi" : "Pasif"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* --- FORM DIALOGS / MODALS --- */}

      {/* 1. PRODUCT ADD / EDIT DIALOG */}
      {isProductModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="relative w-full max-w-4xl rounded-3xl border border-gray-200 bg-white p-6 shadow-xl max-h-[95vh] overflow-y-auto flex flex-col justify-between">
            {/* Modal Head */}
            <button
              onClick={() => setIsProductModalOpen(false)}
              className="absolute right-4 top-4 rounded-xl p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <X className="size-5" />
            </button>

            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-1.5">
              <span>
                {editingProduct ? `"${prodFormName}" Ürününü Düzenle` : "Menüye Yeni Ürün Ekle"}
              </span>
            </h3>

            {/* Split layout inside Modal: Form left, Live Preview right */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Form Input fields */}
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase">
                      Ürün Adı *
                    </label>
                    <input
                      type="text"
                      value={prodFormName}
                      onChange={(e) => setProdFormName(e.target.value)}
                      placeholder="Espresso, Tiramisu vb."
                      className="w-full rounded-xl border border-gray-200 bg-gray-55 px-3.5 py-2 text-sm focus:outline-none focus:border-gray-700 focus:bg-white"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase">
                      Kategori *
                    </label>
                    <select
                      value={prodFormCategoryId}
                      onChange={(e) => setProdFormCategoryId(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 bg-gray-55 px-3 py-2 text-sm focus:outline-none focus:border-gray-700 focus:bg-white font-bold"
                    >
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase">
                      Fiyat ($) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={prodFormPrice}
                      onChange={(e) => setProdFormPrice(parseFloat(e.target.value) || 0)}
                      className="w-full rounded-xl border border-gray-200 bg-gray-55 px-3.5 py-2 text-sm focus:outline-none focus:border-gray-700 focus:bg-white font-extrabold text-gray-800"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-gray-500 uppercase">
                      Mutfak Hazırlık İstasyonu
                    </label>
                    <select
                      value={prodFormStation}
                      onChange={(e) => setProdFormStation(e.target.value)}
                      className="w-full rounded-xl border border-gray-250 bg-gray-55 px-3 py-2 text-sm focus:outline-none focus:border-gray-700 focus:bg-white font-bold text-gray-750"
                    >
                      {stations.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-[10px] font-bold text-gray-500 uppercase">
                    Açıklama / Alerjen Notları
                  </label>
                  <textarea
                    value={prodFormDesc}
                    onChange={(e) => setProdFormDesc(e.target.value)}
                    placeholder="Premium Arabica kahve çekirdekleri..."
                    rows={2}
                    className="w-full rounded-xl border border-gray-200 bg-gray-55 px-3.5 py-2 text-xs focus:outline-none focus:border-gray-700 focus:bg-white resize-none"
                  />
                </div>

                {/* Connect Modifier option groups checkboxes */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-gray-500 uppercase block">
                    Ekstra Seçenek Grupları
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {optionGroups.map((g) => {
                      const isConnected = prodFormOptionGroups.includes(g.id);
                      return (
                        <button
                          key={g.id}
                          type="button"
                          onClick={() => {
                            if (isConnected) {
                              setProdFormOptionGroups(
                                prodFormOptionGroups.filter((id) => id !== g.id),
                              );
                            } else {
                              setProdFormOptionGroups([...prodFormOptionGroups, g.id]);
                            }
                          }}
                          className={`text-xs px-3 py-1.5 rounded-xl border font-bold transition-all ${
                            isConnected
                              ? "bg-gray-50 text-gray-800 border-gray-200 shadow-sm animate-pulse"
                              : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          {g.name}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Icon Grid Picker */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-gray-500 uppercase block">
                    Görsel İkon
                  </label>
                  <div className="grid grid-cols-8 gap-1.5 p-2 bg-gray-50 border border-gray-200 rounded-xl max-h-[85px] overflow-y-auto">
                    {AVAILABLE_ICONS.map((item) => {
                      const isSelected = prodFormIconName === item.name;
                      const IconComp = item.icon;
                      return (
                        <button
                          key={item.name}
                          type="button"
                          onClick={() => setProdFormIconName(item.name)}
                          className={`p-1.5 rounded-lg flex items-center justify-center border transition-all ${
                            isSelected
                              ? "bg-gray-900 text-white border-gray-900 scale-105"
                              : "bg-white text-gray-550 border-gray-150 hover:bg-gray-100"
                          }`}
                        >
                          <IconComp className="size-4" />
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Color Background classes picker */}
                <div className="space-y-1.5">
                  <label className="text-[10px] font-bold text-gray-500 uppercase block">
                    İkon Kart Rengi
                  </label>
                  <div className="grid grid-cols-7 gap-1.5 p-2 bg-gray-50 border border-gray-200 rounded-xl">
                    {ICON_COLORS.map((color) => {
                      const isSelected = prodFormColorClass === color.class;
                      return (
                        <button
                          key={color.name}
                          type="button"
                          onClick={() => setProdFormColorClass(color.class)}
                          className={`h-7 rounded-lg border transition-all ${color.class} ${
                            isSelected
                              ? "border-black border-2 scale-105"
                              : "border-gray-200 hover:opacity-85"
                          }`}
                          title={color.name}
                        />
                      );
                    })}
                  </div>
                </div>

                {/* Toggles */}
                <div className="flex items-center gap-6 pt-1">
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={prodFormActive}
                      onChange={(e) => setProdFormActive(e.target.checked)}
                      className="rounded border-gray-300 text-gray-900 focus:ring-gray-400 size-4"
                    />
                    <span className="text-xs font-bold text-gray-700">Satışa Açık (Aktif)</span>
                  </label>

                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={prodFormFavorite}
                      onChange={(e) => setProdFormFavorite(e.target.checked)}
                      className="rounded border-gray-300 text-gray-900 focus:ring-gray-400 size-4"
                    />
                    <span className="text-xs font-bold text-gray-700 flex items-center gap-1">
                      <Star className="size-3.5 text-amber-500 fill-amber-500" />
                      Favori / Öne Çıkar
                    </span>
                  </label>
                </div>
              </div>

              {/* LIVE PREVIEW SCREEN CARD (Right) - UX Refinement! */}
              <div className="w-full lg:w-[280px] border border-gray-200 rounded-3xl bg-gray-55 p-5 flex flex-col justify-center items-center gap-4">
                <span className="text-[10px] font-black text-gray-404 uppercase tracking-widest block text-center">
                  Menü Kart Önizlemesi
                </span>

                {/* Live Card */}
                <div
                  className={`w-full bg-white border border-gray-200 rounded-2xl p-4 shadow-sm space-y-3 relative ${
                    !prodFormActive ? "opacity-60" : ""
                  }`}
                >
                  {/* SOLD OUT overlay inside preview */}
                  {!prodFormActive && (
                    <div className="absolute inset-0 bg-[#ffffff99] rounded-2xl flex items-center justify-center z-10 pointer-events-none">
                      <div className="border-2 border-red-500 text-red-500 font-extrabold text-[11px] uppercase px-2 py-0.5 rounded rotate-12 bg-white shadow-xs">
                        TÜKENDİ
                      </div>
                    </div>
                  )}

                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`flex size-9 items-center justify-center rounded-xl font-semibold shadow-inner ${prodFormColorClass}`}
                      >
                        <LivePreviewIconComp className="size-4.5" />
                      </div>
                      <div>
                        <h4 className="font-extrabold text-xs text-gray-900 leading-tight">
                          {prodFormName || "Ürün Adı"}
                        </h4>
                        <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">
                          {categories.find((c) => c.id === prodFormCategoryId)?.label || "Kategori"}
                        </span>
                      </div>
                    </div>

                    {prodFormFavorite && (
                      <Star className="size-3.5 text-amber-500 fill-amber-500" />
                    )}
                  </div>

                  <p className="text-[11px] text-gray-455 line-clamp-2 leading-relaxed">
                    {prodFormDesc || "Açıklama girilmemiş."}
                  </p>

                  <div className="pt-2 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-sm font-black text-gray-955">
                      {formatTRY(prodFormPrice)}
                    </span>
                    <span className="text-[9px] font-bold text-gray-555 bg-gray-100 px-1.5 py-0.5 rounded">
                      {stations.find((s) => s.id === prodFormStation)?.name || "İstasyon"}
                    </span>
                  </div>
                </div>

                <div className="text-center text-[10px] text-gray-400">
                  Form verilerini değiştirdiğinizde kart önizlemesi gerçek zamanlı olarak
                  güncellenir.
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="pt-4 border-t border-gray-150 flex items-center justify-end gap-2 mt-4">
              <Button
                variant="outline"
                className="text-xs font-bold rounded-xl border-gray-200 text-gray-655 hover:bg-gray-55 px-4 py-2"
                onClick={() => setIsProductModalOpen(false)}
              >
                İptal
              </Button>
              <button
                className="text-xs font-extrabold rounded-xl bg-gray-900 text-white hover:bg-gray-800 px-5 py-2 shadow-sm transition-all"
                onClick={saveProduct}
              >
                Ürünü Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 2. CATEGORY DIALOG */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-xl space-y-4">
            <button
              onClick={() => setIsCategoryModalOpen(false)}
              className="absolute right-4 top-4 rounded-xl p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <X className="size-5" />
            </button>

            <h3 className="text-lg font-bold text-gray-955">
              {editingCategory ? "Kategoriyi Düzenle" : "Yeni Kategori Ekle"}
            </h3>

            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">
                  Kategori Başlığı *
                </label>
                <input
                  type="text"
                  value={catFormLabel}
                  onChange={(e) => setCatFormLabel(e.target.value)}
                  placeholder="örn: Kahveler, Burgerler"
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2 text-sm focus:outline-none focus:border-gray-700 focus:bg-white"
                />
              </div>

              {/* Icon Selector Grid */}
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-500 uppercase block">
                  Kategori İkonu
                </label>
                <div className="grid grid-cols-4 gap-2 p-2 bg-gray-55 border border-gray-200 rounded-xl max-h-[120px] overflow-y-auto">
                  {AVAILABLE_ICONS.map((item) => {
                    const TargetIcon = item.icon;
                    const isSelected = catFormIconName === item.name;
                    return (
                      <button
                        key={item.name}
                        type="button"
                        onClick={() => setCatFormIconName(item.name)}
                        className={`p-2.5 rounded-xl flex items-center justify-center border transition-all ${
                          isSelected
                            ? "bg-gray-900 text-white border-gray-900 shadow"
                            : "bg-white text-gray-550 border-gray-200 hover:bg-gray-100"
                        }`}
                      >
                        <TargetIcon className="size-5" />
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                className="text-xs font-bold rounded-xl border-gray-250 text-gray-655 hover:bg-gray-55 px-4 py-2"
                onClick={() => setIsCategoryModalOpen(false)}
              >
                İptal
              </Button>
              <button
                className="text-xs font-extrabold rounded-xl bg-gray-900 text-white hover:bg-gray-800 px-5 py-2 shadow-sm transition-all"
                onClick={saveCategory}
              >
                Kategori Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 3. OPTION GROUP / MODIFIER DIALOG */}
      {isOptionGroupModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="relative w-full max-w-xl rounded-2xl border border-gray-200 bg-white p-6 shadow-xl space-y-4 max-h-[90vh] overflow-y-auto flex flex-col justify-between">
            <div>
              <button
                onClick={() => setIsOptionGroupModalOpen(false)}
                className="absolute right-4 top-4 rounded-xl p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              >
                <X className="size-5" />
              </button>

              <h3 className="text-lg font-bold text-gray-955">
                {editingOptionGroup ? "Seçenek Grubunu Düzenle" : "Yeni Seçenek Grubu Oluştur"}
              </h3>

              <div className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase">
                    Grup Başlığı *
                  </label>
                  <input
                    type="text"
                    value={optGroupName}
                    onChange={(e) => setOptGroupName(e.target.value)}
                    placeholder="Süt Seçimi, Şurup Ekleme vb."
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2 text-sm focus:outline-none focus:border-gray-700 focus:bg-white font-bold"
                  />
                </div>

                {/* Min / Max selections */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">
                      Min Seçim (0 ise isteğe bağlı)
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={optGroupMin}
                      onChange={(e) => setOptGroupMin(parseInt(e.target.value) || 0)}
                      className="w-full rounded-xl border border-gray-250 bg-gray-55 px-3.5 py-2 text-sm focus:outline-none focus:border-gray-700"
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-550 uppercase">Max Seçim</label>
                    <input
                      type="number"
                      min="1"
                      value={optGroupMax}
                      onChange={(e) => setOptGroupMax(parseInt(e.target.value) || 1)}
                      className="w-full rounded-xl border border-gray-250 bg-gray-55 px-3.5 py-2 text-sm focus:outline-none focus:border-gray-700"
                    />
                  </div>
                </div>

                {/* Options List editor */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                    <label className="text-xs font-bold text-gray-550 uppercase">
                      Seçenek Öğeleri ({optGroupItems.length})
                    </label>
                    <button
                      type="button"
                      onClick={addOptionItemRow}
                      className="text-xs font-bold text-gray-900 hover:text-gray-800 flex items-center gap-0.5"
                    >
                      <PlusCircle className="size-3.5" />
                      Seçenek Satırı Ekle
                    </button>
                  </div>

                  {optGroupItems.length === 0 ? (
                    <div className="text-center py-8 text-gray-400 text-xs border border-dashed border-gray-250 rounded-xl">
                      Hiç alt seçenek bulunmuyor. Satır Ekle butonunu kullanın.
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                      {optGroupItems.map((item) => (
                        <div key={item.id} className="flex items-center gap-2">
                          <input
                            type="text"
                            placeholder="Seçenek Adı (örn: Yulaf Sütü)"
                            value={item.name}
                            onChange={(e) => updateOptionItemRow(item.id, "name", e.target.value)}
                            className="flex-1 rounded-lg border border-gray-200 bg-gray-55 px-3 py-1.5 text-xs focus:outline-none focus:border-gray-700"
                          />
                          <div className="relative w-28">
                            <span className="absolute left-2.5 top-1.5 text-xs text-gray-404 font-bold">
                              $
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              placeholder="Fiyat Farkı"
                              value={item.priceDelta}
                              onChange={(e) =>
                                updateOptionItemRow(
                                  item.id,
                                  "priceDelta",
                                  parseFloat(e.target.value) || 0,
                                )
                              }
                              className="w-full rounded-lg border border-gray-250 bg-gray-55 pl-6 pr-2 py-1.5 text-xs focus:outline-none focus:border-gray-700 font-semibold"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeOptionItemRow(item.id)}
                            className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2 mt-4">
              <Button
                variant="outline"
                className="text-xs font-bold rounded-xl border-gray-250 text-gray-600 hover:bg-gray-50 px-4 py-2"
                onClick={() => setIsOptionGroupModalOpen(false)}
              >
                İptal
              </Button>
              <button
                className="text-xs font-extrabold rounded-xl bg-gray-900 text-white hover:bg-gray-800 px-5 py-2 shadow-sm transition-all"
                onClick={saveOptionGroup}
              >
                Grubu Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 4. PREP STATION DIALOG */}
      {isStationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
          <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-xl space-y-4">
            <button
              onClick={() => setIsStationModalOpen(false)}
              className="absolute right-4 top-4 rounded-xl p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <X className="size-5" />
            </button>

            <h3 className="text-lg font-bold text-gray-955">
              {editingStation ? "İstasyonu Düzenle" : "Yeni Hazırlık İstasyonu Ekle"}
            </h3>

            <div className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">İstasyon Adı *</label>
                <input
                  type="text"
                  value={stationName}
                  onChange={(e) => setStationName(e.target.value)}
                  placeholder="örn: Sıcak Mutfak, Bar"
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2 text-sm focus:outline-none focus:border-gray-700 focus:bg-white"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">
                  IP Yazıcı Bağlantısı
                </label>
                <input
                  type="text"
                  value={stationPrinter}
                  onChange={(e) => setStationPrinter(e.target.value)}
                  placeholder="örn: 192.168.1.50 (KOT-1)"
                  className="w-full rounded-xl border border-gray-250 bg-gray-50 px-3.5 py-2 text-sm focus:outline-none focus:border-gray-700 focus:bg-white "
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold text-gray-500 uppercase">İstasyon Tipi</label>
                <select
                  value={stationType}
                  onChange={(e) => setStationType(e.target.value as any)}
                  className="w-full rounded-xl border border-gray-250 bg-gray-55 px-3 py-2 text-sm focus:outline-none focus:border-gray-700"
                >
                  <option value="general">Genel / Karışık</option>
                  <option value="drinks">Sıcak & Soğuk Barista Bar</option>
                  <option value="food">Mutfak Yemek Hattı</option>
                  <option value="bakery">Fırın & Tatlı Tezgâhı</option>
                  <option value="grill">Izgara & Sıcak Bölüm</option>
                </select>
              </div>

              <label className="flex items-center gap-2 cursor-pointer pt-1">
                <input
                  type="checkbox"
                  checked={stationActive}
                  onChange={(e) => setStationActive(e.target.checked)}
                  className="rounded border-gray-350 text-gray-900 focus:ring-gray-400 size-4"
                />
                <span className="text-xs font-bold text-gray-700">İstasyon Çevrimiçi (Aktif)</span>
              </label>
            </div>

            <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
              <Button
                variant="outline"
                className="text-xs font-bold rounded-xl border-gray-250 text-gray-655 hover:bg-gray-55 px-4 py-2"
                onClick={() => setIsStationModalOpen(false)}
              >
                İptal
              </Button>
              <button
                className="text-xs font-extrabold rounded-xl bg-gray-900 text-white hover:bg-gray-800 px-5 py-2 shadow-sm transition-all"
                onClick={saveStation}
              >
                İstasyonu Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
